import React from 'react';

/**
 * Renders a JSON value with proper formatting and styling
 * @param value - The value to render
 * @param language - The language to use for date formatting (optional)
 * @returns Formatted React node
 */
export const renderJsonValue = (value: unknown, language = 'en'): React.ReactNode => {
  if (value === null) return <span className="text-gray-400">null</span>;
  if (value === undefined) return <span className="text-gray-400">undefined</span>;
  
  if (typeof value === 'boolean') {
    return <span className="text-purple-600">{value.toString()}</span>;
  }
  
  if (typeof value === 'number') {
    return <span className="text-blue-600">{value}</span>;
  }
  
  if (typeof value === 'string') {
    // Check if it looks like a date string
    if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
      return <span className="text-green-600">{value}</span>;
    }
    
    // Check if it's a sensitive field
    if (
      /apikey|secret|password|token|key/i.test(value) && 
      value.length > 8
    ) {
      return <span className="text-green-600">"********"</span>;
    }
    
    return <span className="text-green-600">"{value}"</span>;
  }
  
  if (Array.isArray(value)) {
    return (
      <div className="pl-4 border-l-2 border-gray-200">
        [
        {value.map((item, index) => (
          <div key={index} className="pl-4">
            {renderJsonValue(item, language)}
            {index < value.length - 1 && ','}
          </div>
        ))}
        ]
      </div>
    );
  }
  
  if (typeof value === 'object' && value !== null) {
    return (
      <div className="pl-4 border-l-2 border-gray-200">
        {'{'}
        {Object.entries(value).map(([key, val], index, arr) => {
          // Check if the key suggests sensitive data
          const isSensitive = /apikey|secret|password|token|key/i.test(key);
          
          return (
            <div key={key} className="pl-4">
              <span className="text-gray-700">"{key}"</span>: {
                isSensitive && typeof val === 'string' 
                  ? <span className="text-green-600">"********"</span>
                  : renderJsonValue(val, language)
              }
              {index < arr.length - 1 && ','}
            </div>
          );
        })}
        {'}'}
      </div>
    );
  }
  
  return String(value);
};

/**
 * Renders a JSON object in a collapsible section
 * @param data - The data object to render
 * @param title - Optional title for the section
 * @param language - The language to use for date formatting
 * @returns Formatted React component
 */
export const JsonViewer: React.FC<{
  data: unknown;
  title?: string;
  language?: string;
}> = ({ data, title, language = 'en' }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {title && (
        <div 
          className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h3 className="text-sm font-medium text-gray-700">{title}</h3>
          <button className="text-gray-500 hover:text-gray-700">
            {isExpanded ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
        </div>
      )}
      
      {isExpanded && (
        <div className="p-4 font-mono text-sm overflow-x-auto bg-gray-50">
          {renderJsonValue(data, language)}
        </div>
      )}
    </div>
  );
};

/**
 * Renders a button to log data to console
 * @param data - The data to log
 * @param label - Button label
 * @returns Button component
 */
export const ConsoleLogButton: React.FC<{
  data: unknown;
  label?: string;
}> = ({ data, label = 'Log to console' }) => {
  return (
    <button 
      onClick={() => console.log(data)}
      className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
    >
      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {label}
    </button>
  );
}; 